import { z } from "zod";

const semantic = /^v?(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/;
export function compareVersions(left: string, right: string): number | null {
  if (!semantic.test(left) || !semantic.test(right)) return null;
  const a = left.replace(/^v/, "").split(".").map(BigInt);
  const b = right.replace(/^v/, "").split(".").map(BigInt);
  for (const [i, part] of a.entries())
    if (part !== b[i]) return part > b[i]! ? 1 : -1;
  return 0;
}
const note = z.object({
  category: z.enum(["feature", "improvement", "fix", "internal"]),
  text: z.string().min(1).max(2000),
  userFacing: z.boolean(),
});
const release = z.object({
  version: z.string().regex(semantic),
  releasedAt: z.iso.datetime({ offset: true }),
  notes: z.array(note),
});
export const componentSchema = z.object({
  schemaVersion: z.literal(1),
  component: z.enum(["frontend", "backend"]),
  version: z.string().min(1).max(64),
  releasedAt: z.iso.datetime({ offset: true }).nullable(),
  releases: z.array(release),
});
export const appInformationSchema = z.object({
  frontend: componentSchema
    .extend({ component: z.literal("frontend") })
    .nullable(),
  backend: componentSchema.extend({ component: z.literal("backend") }),
  policy: z
    .object({
      enforcementEnabled: z.boolean(),
      minimumSupportedFrontendVersion: z.string().regex(semantic).nullable(),
    })
    .refine(
      (p) =>
        !p.enforcementEnabled || p.minimumSupportedFrontendVersion !== null,
    ),
});
export type ComponentRelease = z.infer<typeof componentSchema>;
export type Release = z.infer<typeof release>;
export type AppInformation = z.infer<typeof appInformationSchema>;
export function needsRequiredUpdate(
  installed: string,
  policy: AppInformation["policy"],
) {
  if (!policy.enforcementEnabled || !policy.minimumSupportedFrontendVersion)
    return false;
  const comparison = compareVersions(
    installed,
    policy.minimumSupportedFrontendVersion,
  );
  return comparison === null || comparison < 0;
}
/** Announcements contain product notes only; they never retain a route or user identity. */
export function unseenReleases(
  component: ComponentRelease | null,
  seen: string | null,
): Release[] {
  if (!component || seen === component.version) return [];
  return component.releases.filter(
    (r) =>
      r.notes.some((n) => n.userFacing) &&
      (compareVersions(r.version, component.version) ?? 1) <= 0 &&
      (seen === null
        ? r.version === component.version
        : (compareVersions(r.version, seen) ?? 0) > 0),
  );
}
